import { useEffect, useMemo, useRef, useState } from "react";
import prototypeHtml from "../../prototypes/getproof_landing_prototype_split_hero.html?raw";
import heroImage from "@/landing/assets/hero-creche-trust-v1.png";
import jeremyPhoto from "@/landing/assets/jeremy-arzoumanian.jpeg";
import vivienPhoto from "@/landing/assets/vivien-darakjian.jpeg";
import michaelPhoto from "@/landing/assets/michaël-baratian.jpeg";
import elyoPhoto from "@/landing/assets/elyo-salloum.jpeg";
import robinPhoto from "@/landing/assets/robin-catala.png";
import mohamedPhoto from "@/landing/assets/mohamed-chenatra .png";
import marouanePhoto from "@/landing/assets/marouane-elgueddah.png";
import guillaumePhoto from "@/landing/assets/guillaume-coudiere.png";

const demoUrl = "https://cal.com/dimitri-ndeke/demo-getproof?lang=fr";
const demoEmbedUrl = "https://cal.com/dimitri-ndeke/demo-getproof?embed=true&layout=month_view&lang=fr";

export default function HtmlPrototypeLanding() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [iframeHeight, setIframeHeight] = useState(2200);
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  const srcDoc = useMemo(() => {
    return prototypeHtml
      .replaceAll("../public/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png", "/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png")
      .replaceAll("../src/landing/assets/hero-creche-trust-v1.png", heroImage)
      .replaceAll("../src/landing/assets/jeremy-arzoumanian.jpeg", jeremyPhoto)
      .replaceAll("../src/landing/assets/vivien-darakjian.jpeg", vivienPhoto)
      .replaceAll("../src/landing/assets/michaël-baratian.jpeg", michaelPhoto)
      .replaceAll("../src/landing/assets/elyo-salloum.jpeg", elyoPhoto)
      .replaceAll("../src/landing/assets/robin-catala.png", robinPhoto)
      .replaceAll("../src/landing/assets/mohamed-chenatra .png", mohamedPhoto)
      .replaceAll("../src/landing/assets/marouane-elgueddah.png", marouanePhoto)
      .replaceAll("../src/landing/assets/guillaume-coudiere.png", guillaumePhoto)
      .replace(
        "</head>",
        `<base href="${window.location.origin}/"><style>#demoModalOverlay{display:none!important;}</style></head>`
      )
      .replace(
        "const demoBookingUrl = 'https://cal.com/dimitri-ndeke/demo-getproof?embed=true&layout=month_view&lang=fr';",
        `const demoBookingUrl = '${demoEmbedUrl}';`
      )
      .replace(
        `function openDemoModal() {
      demoModalFrame.src = demoBookingUrl;
      demoModalOverlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }`,
        `function openDemoModal() {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'getproof-book-demo' }, '*');
        return;
      }
      demoModalFrame.src = demoBookingUrl;
      demoModalOverlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }`
      );
  }, []);

  useEffect(() => {
    document.body.style.overflowX = "hidden";
    document.body.style.overflowY = isDemoOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflowX = "";
      document.body.style.overflowY = "";
    };
  }, [isDemoOpen]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const frameWindow = iframeRef.current?.contentWindow;
      if (event.source !== frameWindow) return;
      if (event.data?.type === "getproof-book-demo") {
        setIsDemoOpen(true);
      }
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsDemoOpen(false);
      }
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener("keydown", handleKeydown);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame) return;

    const clearCurrent = () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };

    const attachSizing = () => {
      clearCurrent();

      const currentFrame = iframeRef.current;
      const doc = currentFrame?.contentDocument;
      const win = currentFrame?.contentWindow;
      if (!currentFrame || !doc || !win) return;

      const syncHeight = () => {
        const nextHeight = Math.max(
          doc.body?.scrollHeight ?? 0,
          doc.documentElement?.scrollHeight ?? 0,
          doc.body?.offsetHeight ?? 0,
          doc.documentElement?.offsetHeight ?? 0
        );

        if (nextHeight > 0) {
          setIframeHeight(nextHeight);
        }
      };

      const demoButtons = Array.from(
        doc.querySelectorAll<HTMLElement>("[data-book-demo]")
      );

      demoButtons.forEach((button) => {
        button.setAttribute("href", demoUrl);
        button.setAttribute("target", "_top");
        button.setAttribute("rel", "noreferrer");
      });

      syncHeight();
      win.requestAnimationFrame(syncHeight);
      win.setTimeout(syncHeight, 250);
      win.setTimeout(syncHeight, 1000);

      const observer = new ResizeObserver(syncHeight);
      if (doc.body) observer.observe(doc.body);
      if (doc.documentElement) observer.observe(doc.documentElement);

      win.addEventListener("resize", syncHeight);
      cleanupRef.current = () => {
        observer.disconnect();
        win.removeEventListener("resize", syncHeight);
      };
    };

    frame.addEventListener("load", attachSizing);
    return () => {
      frame.removeEventListener("load", attachSizing);
      clearCurrent();
    };
  }, [srcDoc]);

  return (
    <>
      <iframe
        ref={iframeRef}
        title="GetProof landing"
        srcDoc={srcDoc}
        style={{
          display: "block",
          width: "100%",
          minHeight: "100vh",
          height: `${iframeHeight}px`,
          border: "0",
          background: "transparent",
        }}
      />

      {isDemoOpen ? (
        <div
          onClick={() => setIsDemoOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            background: "rgba(15, 23, 42, 0.44)",
            backdropFilter: "blur(6px)",
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: "relative",
              width: "min(100%, 920px)",
              height: "min(85vh, 720px)",
              borderRadius: "24px",
              overflow: "hidden",
              background: "#fff",
              boxShadow: "0 30px 90px rgba(15, 23, 42, 0.24)",
            }}
          >
            <button
              type="button"
              onClick={() => setIsDemoOpen(false)}
              aria-label="Fermer"
              style={{
                position: "absolute",
                top: "14px",
                right: "14px",
                zIndex: 1,
                width: "36px",
                height: "36px",
                borderRadius: "999px",
                border: "none",
                background: "#fff",
                color: "#10233b",
                fontSize: "1.1rem",
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                boxShadow: "0 8px 20px rgba(16, 35, 59, 0.18)",
              }}
            >
              ×
            </button>
            <iframe
              title="Planifier une démo"
              src={demoEmbedUrl}
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
